import React, { useState } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
    Card,
    CardContent,
    Grid,
    Typography,
    Checkbox,
    Box,
    IconButton,
    Tooltip,
    Divider,
    Collapse,
    Chip, // New
    Stack, // New
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import type { KeywordCategoryKey, KeywordHits, PubMedArticle, MatchedKeywordFamily } from './types'
import type { DuplicateInfo } from './PubMedUtilities'

interface ArticleCardProps extends PubMedArticle {
    keywordHits?: KeywordHits

    /**
     * Structured category -> measure -> threshold matches produced during
     * PubMed enrichment.
     *
     * ArticleCard uses these for detailed tooltips while retaining
     * keywordHits as a backward-compatible fallback.
     */
    matchedKeywordFamilies?: MatchedKeywordFamily[]

    searchTermsScore?: number
    customKeywordsScore?: number
    interventionMatches?: string[]
    outcomeMatches?: string[]
    studyGeographyLabels?: string[]

    selected?: boolean
    onSelect?: (pmid: string, selected: boolean) => void

    duplicateInfo?: DuplicateInfo
}

const ArticleCard: React.FC<ArticleCardProps> = ({
    pmid,
    pmcId,
    doi,
    title,
    authors = [],
    authorDetails = [],
    affiliations = [],
    journal,
    publicationDate,
    pages,
    abstract,
    shortCitation,
    longCitation,
    publicationTypes = [],
    industryAffiliations = [],
    interventionMatches = [],
    studyGeographyLabels = [],
    // chemicals = [],
    hasFreePMC,
    selected = false,
    onSelect,

    // NEW optional props
    searchTermsScore,
    matchedKeywordFamilies,
    customKeywordsScore,
    keywordHits,
    duplicateInfo,
}) => {
    const handleCopyReference = () => {
        if (longCitation) {
            navigator.clipboard.writeText(longCitation)
            return
        }

        if (shortCitation) {
            navigator.clipboard.writeText(shortCitation)
            return
        }

        const cleanTitle = (title || '').trim().replace(/[.\s]+$/, '')
        const cleanJournal = (journal || '').trim().replace(/[.\s]+$/, '')

        const citationParts = [
            cleanTitle ? `${cleanTitle}.` : '',
            cleanJournal ? `${cleanJournal}${pages ? `, ${pages}` : ''}.` : '',
            `DOI: ${doi || 'N/A'}`,
        ].filter(Boolean)

        navigator.clipboard.writeText(citationParts.join(' '))
    }
    const [expanded, setExpanded] = useState(false)
    const [affiliationsExpanded, setAffiliationsExpanded] = useState(false)

    /**
     * NEW: helper to render category chips when keyword hits are available.
     */

    type KeywordMeasureGroup = {
        measureId: string
        measureLabel: string
        thresholds: string[]
        familyLabels: string[]
        matchedTerms: string[]
    }

    function uniqueNonEmpty(values: Array<string | undefined>): string[] {
        return Array.from(
            new Set(values.map((value) => value?.trim()).filter((value): value is string => Boolean(value)))
        )
    }

    function buildMeasureGroups(matches: MatchedKeywordFamily[]): KeywordMeasureGroup[] {
        const groups = new Map<string, KeywordMeasureGroup>()

        matches.forEach((match) => {
            /**
             * Families that do not yet have hierarchy metadata use their own
             * family ID and label as a standalone measure group.
             */
            const measureId = match.measureId || match.familyId
            const measureLabel = match.measureLabel || match.thresholdLabel || match.familyLabel

            const existing = groups.get(measureId) || {
                measureId,
                measureLabel,
                thresholds: [],
                familyLabels: [],
                matchedTerms: [],
            }

            if (match.thresholdLabel && match.thresholdLabel !== measureLabel) {
                existing.thresholds.push(match.thresholdLabel)
            }

            /**
             * Preserve standalone family labels when they carry information not
             * already represented by the measure or threshold label.
             */
            if (!match.thresholdLabel && match.familyLabel !== measureLabel) {
                existing.familyLabels.push(match.familyLabel)
            }

            existing.matchedTerms.push(...match.matchedTerms)

            existing.thresholds = uniqueNonEmpty(existing.thresholds)
            existing.familyLabels = uniqueNonEmpty(existing.familyLabels)
            existing.matchedTerms = uniqueNonEmpty(existing.matchedTerms)

            groups.set(measureId, existing)
        })

        return Array.from(groups.values()).sort((a, b) => a.measureLabel.localeCompare(b.measureLabel))
    }

    function buildKeywordTooltip(
        categoryLabel: string,
        structuredMatches: MatchedKeywordFamily[],
        fallbackMatches: string[]
    ): React.ReactNode {
        if (structuredMatches.length === 0) {
            return fallbackMatches.length > 0 ? `${categoryLabel}: ${fallbackMatches.join(', ')}` : categoryLabel
        }

        const measureGroups = buildMeasureGroups(structuredMatches)

        return (
            <Box sx={{ maxWidth: 420 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                    {categoryLabel}
                </Typography>

                <Stack spacing={0.75}>
                    {measureGroups.map((group) => {
                        const childLabels = uniqueNonEmpty([...group.thresholds, ...group.familyLabels])

                        return (
                            <Box key={group.measureId}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {group.measureLabel}
                                </Typography>

                                {childLabels.length > 0 && (
                                    <Box
                                        component="ul"
                                        sx={{
                                            pl: 2.25,
                                            mt: 0.25,
                                            mb: 0,
                                        }}
                                    >
                                        {childLabels.map((label) => (
                                            <Typography
                                                component="li"
                                                variant="caption"
                                                key={label}
                                                sx={{
                                                    display: 'list-item',
                                                }}
                                            >
                                                {label}
                                            </Typography>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        )
                    })}
                </Stack>
            </Box>
        )
    }

    const renderKeywordChips = () => {
        /**
         * Prefer the new structured matches, but retain keywordHits as a fallback
         * for cached, historical, or partially enriched article records.
         */
        const hasStructuredMatches = Array.isArray(matchedKeywordFamilies) && matchedKeywordFamilies.length > 0

        if (!hasStructuredMatches && !keywordHits) {
            return null
        }

        /**
         * Safety remains available for scoring, filters, and exports, but is not
         * displayed as an article-card chip.
         */
        const chipConfig: Partial<
            Record<
                KeywordCategoryKey,
                {
                    label: string
                    tooltipLabel: string
                }
            >
        > = {
            clinicalEfficacy: {
                label: 'Clinical',
                tooltipLabel: 'Clinical Efficacy and Effectiveness',
            },
            humanistic: {
                label: 'PROs',
                tooltipLabel: 'Patient-Reported and Humanistic Outcomes',
            },
            economic: {
                label: 'Economic',
                tooltipLabel: 'Economic Outcomes',
            },
            treatmentPatterns: {
                label: 'Treatment Patterns',
                tooltipLabel: 'Treatment Patterns',
            },
            epidemiology: {
                label: 'Epidemiology',
                tooltipLabel: 'Epidemiology and Disease Burden',
            },
            competitors: {
                label: 'Competitors',
                tooltipLabel: 'Competitor Treatments',
            },
        }

        const visibleCategories = Object.keys(chipConfig) as KeywordCategoryKey[]

        const chips = visibleCategories.flatMap((category) => {
            const config = chipConfig[category]

            if (!config) {
                return []
            }

            const structuredCategoryMatches = hasStructuredMatches
                ? matchedKeywordFamilies!.filter((match) => match.category === category)
                : []

            const fallbackEntry = keywordHits?.[category]
            const fallbackMatches = fallbackEntry?.matches || []

            const categoryHasMatch = structuredCategoryMatches.length > 0 || Boolean(fallbackEntry?.hit)

            if (!categoryHasMatch) {
                return []
            }

            const tooltip = buildKeywordTooltip(config.tooltipLabel, structuredCategoryMatches, fallbackMatches)

            return [
                <Tooltip key={category} title={tooltip} arrow placement="top" enterDelay={250}>
                    <Chip size="small" label={config.label} />
                </Tooltip>,
            ]
        })

        if (chips.length === 0) {
            return null
        }

        return (
            <Stack
                direction="row"
                spacing={1}
                sx={{
                    flexWrap: 'wrap',
                    gap: 1,
                }}
            >
                {chips}
            </Stack>
        )
    }

    /**
     * NEW: helper to show both scores (if provided) in a compact way.
     * These will later correspond to the two sorting modes in PubMedView:
     *  - searchTermsScore = default sort
     *  - customKeywordsScore = alternate sort
     */
    const renderScores = () => {
        const hasAnyScore = typeof searchTermsScore === 'number' || typeof customKeywordsScore === 'number'
        if (!hasAnyScore) return null

        return (
            <Box>
                <Typography variant="body2" color="text.secondary">
                    <strong>Match Scores:</strong>{' '}
                    {typeof searchTermsScore === 'number' ? (
                        <>
                            Search: <strong>{searchTermsScore}</strong>
                        </>
                    ) : (
                        <>
                            Search: <strong>—</strong>
                        </>
                    )}
                    {'  |  '}
                    {typeof customKeywordsScore === 'number' ? (
                        <>
                            Keyword: <strong>{customKeywordsScore}</strong>
                        </>
                    ) : (
                        <>
                            Keyword: <strong>—</strong>
                        </>
                    )}
                </Typography>
            </Box>
        )
    }

    // NOTE: Abstract can be undefined/null based on PubMed record types
    const abstractText = abstract ? abstract : ''
    const hasLongAbstract = abstractText.length > 150

    return (
        <Card
            sx={{
                display: 'flex',
                flexDirection: 'row',
                p: 2,
                mb: 2,
                borderRadius: 2,
                boxShadow: 3,
                alignItems: 'flex-start',
                backgroundColor: selected ? 'rgba(81, 179, 255, 0.15)' : 'transparent',
                transition: 'background-color 0.2s ease',
            }}
        >
            <Box sx={{ pr: 2, pt: 1 }}>
                <Checkbox checked={selected} onChange={(e) => onSelect?.(pmid, e.target.checked)} color="primary" />
            </Box>

            <CardContent sx={{ flexGrow: 1, p: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            fontSize: '0.75rem',
                            lineHeight: 1.4,
                        }}
                    >
                        {publicationDate && (
                            <>
                                <strong>Date:</strong> {publicationDate}
                                {'  |  '}
                            </>
                        )}
                        <strong>PMID:</strong> {pmid}
                        {doi && (
                            <>
                                {'  |  '}
                                <strong>DOI:</strong> {doi}
                            </>
                        )}
                    </Typography>
                    {duplicateInfo?.isDuplicate && (
                        <Tooltip
                            title={`Already exists in your ${duplicateInfo.collection} library (matched by ${duplicateInfo.matchedOn})`}
                            arrow
                        >
                            <WarningAmberIcon sx={{ color: 'error.main', fontSize: 24 }} />
                        </Tooltip>
                    )}
                    {pmcId && (
                        <Tooltip title="Full-text PDF available via PubMed Central — will be auto-imported" arrow>
                            <Chip
                                size="small"
                                label="PMC PDF"
                                color="success"
                                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 'bold' }}
                            />
                        </Tooltip>
                    )}
                </Box>
                <Divider sx={{ my: 1, opacity: 0.3 }} />

                <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {title}
                </Typography>

                <Box sx={{ mt: 0.5, mb: 0.25 }}>
                    <Box
                        onClick={() => {
                            if (affiliations.length > 0) {
                                setAffiliationsExpanded((prev) => !prev)
                            }
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 1,
                            cursor: affiliations.length > 0 ? 'pointer' : 'default',
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: '0.8rem',
                                lineHeight: 1.5,
                                color: 'text.secondary',
                                flexGrow: 1,
                            }}
                        >
                            {authorDetails.length > 0
                                ? authorDetails.map((author, index) => (
                                      <React.Fragment key={`${author.name}-${index}`}>
                                          {index > 0 && ', '}
                                          <span>
                                              {author.name}
                                              {author.affiliationIds?.length > 0 && (
                                                  <sup
                                                      style={{
                                                          marginLeft: '2px',
                                                          fontSize: '0.7em',
                                                      }}
                                                  >
                                                      {author.affiliationIds.join(',')}
                                                  </sup>
                                              )}
                                          </span>
                                      </React.Fragment>
                                  ))
                                : authors}
                        </Typography>

                        {affiliations.length > 0 && (
                            <IconButton
                                size="small"
                                onClick={(event) => {
                                    event.stopPropagation()
                                    setAffiliationsExpanded((prev) => !prev)
                                }}
                                sx={{
                                    p: 0.25,
                                    mt: -0.25,
                                    color: 'figma.grayscale.tier3',
                                    transform: affiliationsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease',
                                }}
                            >
                                <ExpandMoreIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>

                    {affiliations.length > 0 && (
                        <Collapse in={affiliationsExpanded}>
                            <Box
                                sx={{
                                    mt: 0.75,
                                    pl: 1,
                                    pr: 4,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        display: 'block',
                                        fontWeight: 600,
                                        mb: 0.5,
                                    }}
                                >
                                    Affiliations
                                </Typography>

                                {affiliations.map((affiliation) => (
                                    <Typography
                                        key={affiliation.id}
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            display: 'block',
                                            lineHeight: 1.45,
                                            mb: 0.35,
                                        }}
                                    >
                                        <sup>{affiliation.id}</sup> {affiliation.text}
                                    </Typography>
                                ))}
                            </Box>
                        </Collapse>
                    )}
                </Box>

                {/* <Typography variant="body2" sx={{ mb: 1 }}>
                {journal} {pages && `, ${pages}`}
            </Typography> */}
                <Divider sx={{ my: 1, opacity: 0.3 }} />

                <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        <strong>Abstract:</strong>
                    </Typography>

                    <Collapse in={expanded} collapsedSize={72}>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                            {abstract ? abstract : 'No abstract available.'}
                        </Typography>
                    </Collapse>

                    {hasLongAbstract && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1 }}>
                            <IconButton
                                size="small"
                                onClick={() => setExpanded((prev) => !prev)}
                                sx={{
                                    color: 'figma.grayscale.tier3',
                                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease',
                                }}
                            >
                                <ExpandMoreIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    )}

                    {/* NEW: show keyword chips + scores on the same row */}
                    {(keywordHits ||
                        typeof searchTermsScore === 'number' ||
                        typeof customKeywordsScore === 'number') && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                            <Box>{renderKeywordChips()}</Box>
                            <Box>{renderScores()}</Box>
                        </Box>
                    )}
                </Box>
                <Divider sx={{ my: 1, opacity: 0.3 }} />

                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={8}>
                        <Typography variant="body2">
                            <strong>Journal:</strong> {journal ?? 'N/A'}
                            {pages ? `, ${pages}` : ''}
                        </Typography>
                        {/* <Typography variant="body2">
                    <strong>Publication Type:</strong> {publicationTypes.length > 0 ? publicationTypes.join(', ') : 'N/A'}
                    </Typography> */}
                        <Typography variant="body2">
                            <strong>Industry affiliations:</strong>{' '}
                            {industryAffiliations.length > 0 ? industryAffiliations.join(', ') : 'None'}
                        </Typography>
                        {interventionMatches.length > 0 && (
                            <Typography variant="body2">
                                <strong>Interventions:</strong> {interventionMatches.join(', ')}
                            </Typography>
                        )}

                        {studyGeographyLabels.length > 0 && (
                            <Typography variant="body2">
                                <strong>Countries:</strong> {studyGeographyLabels.join(', ')}
                            </Typography>
                        )}

                        {/* <Typography variant="body2">

                        {/* <Typography variant="body2">
                            <strong>Chemicals:</strong> {chemicals.length > 0 ? chemicals.join(', ') : 'N/A'}
                        </Typography> */}
                    </Grid>

                    <Grid item xs={4} container justifyContent="flex-end" alignItems="center" spacing={1}>
                        <Grid item>
                            <Tooltip title="View on PubMed">
                                <IconButton
                                    href={`https://pubmed.ncbi.nlm.nih.gov/${pmid}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    color="primary"
                                >
                                    <OpenInNewIcon />
                                </IconButton>
                            </Tooltip>
                        </Grid>

                        {hasFreePMC && (
                            <Grid item>
                                <Tooltip title="Download Free PMC Article">
                                    <IconButton color="primary">
                                        <CloudDownloadIcon />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        )}

                        <Grid item>
                            <Tooltip title="Copy Reference">
                                <IconButton color="primary" onClick={handleCopyReference}>
                                    <ContentCopyIcon />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

export default ArticleCard
